import { useCallback, useEffect, useRef, useState } from "react";

import {
  getAdminPayrolls,
  getCompanyPayrolls,
} from "../../../../../../network/apis/payroll";
import {
  getAdminPayrollAdjustments,
  getCompanyPayrollAdjustments,
} from "../../../../../../network/apis/payrollAdjustments";
import {
  PAYROLL_ADJUSTMENT_LIST_TYPE_FILTERS,
  PAYROLL_ADJUSTMENT_STATUSES,
} from "../../constants/constants";
import {
  PAYROLL_GENERATE_ALL,
  buildEventRoleRates,
  mapBulkPayrollReport,
  mapPayrollAdjustmentEntries,
  mapSinglePayroll,
} from "../utils/mapPayrollReportData";

// `startDate: null` means "untouched" — the report period then falls back to the event's
// `payroll_start_date`. An explicit "" is a user-cleared field, not the default.
const INITIAL_STATE = {
  open: false,
  applicantIds: [],
  loading: false,
  data: null,
  startDate: null,
  endDate: "",
};

const getPayrollsApi = (portal) =>
  portal === "company" ? getCompanyPayrolls : getAdminPayrolls;

const getPayrollAdjustmentsApi = (portal) =>
  portal === "company" ? getCompanyPayrollAdjustments : getAdminPayrollAdjustments;

const normalizeApplicantIds = (applicantIds) =>
  Array.from(
    new Set(
      (applicantIds || []).filter(
        (id) => id !== null && id !== undefined && id !== "",
      ),
    ),
  );

const useEventPayrollGeneratePayroll = ({
  event,
  eventId,
  portal = "admin",
  lang = "en",
  autoDeductionReason = "",
  initialApplicantIds = [],
} = {}) => {
  // Lazy init from the deep-link URL so a remount (e.g. a language switch) reopens
  // straight into the applicant view instead of flashing the list first.
  const [state, setState] = useState(() => {
    const normalizedIds = normalizeApplicantIds(initialApplicantIds);
    if (!normalizedIds.length) return INITIAL_STATE;
    return {
      ...INITIAL_STATE,
      open: true,
      applicantIds: normalizedIds,
      loading: true,
    };
  });
  const [listFilterParams, setListFilterParams] = useState({});

  const close = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const open = useCallback((applicantIds) => {
    const normalizedIds = normalizeApplicantIds(applicantIds);

    if (!normalizedIds.length) return;

    setState({
      ...INITIAL_STATE,
      open: true,
      applicantIds: normalizedIds,
      loading: true,
    });
  }, []);

  const setStartDate = useCallback((value) => {
    setState((current) => ({
      ...current,
      startDate: value,
      loading: current.open ? true : current.loading,
    }));
  }, []);

  const setEndDate = useCallback((value) => {
    setState((current) => ({
      ...current,
      endDate: value,
      loading: current.open ? true : current.loading,
    }));
  }, []);

  // Server-computed: the earliest of the event's own start and its earliest training
  // start, so the default period covers the days worked during training.
  const defaultStartDate = event?.payroll_start_date || "";
  const startDate = state.startDate ?? defaultStartDate;

  const isGenerateAll =
    state.applicantIds.length === 1 &&
    state.applicantIds[0] === PAYROLL_GENERATE_ALL;
  const isSingle = state.applicantIds.length === 1 && !isGenerateAll;

  const loadPayroll = useCallback(async () => {
    if (!eventId) return null;

    const params = {
      page: -1,
      ...(isGenerateAll
        ? listFilterParams
        : { user_id: state.applicantIds.join(",") }),
      ...(startDate ? { start_date: startDate } : {}),
      ...(state.endDate ? { end_date: state.endDate } : {}),
    };

    const adjustmentDateParams = {
      ...(startDate ? { created_at_start_date: startDate } : {}),
      ...(state.endDate ? { created_at_end_date: state.endDate } : {}),
    };

    const response = await getPayrollsApi(portal)({ eventId, params });
    const data = response?.data?.data || {};
    const payrolls = data.payrolls || [];

    if (isSingle) {
      const singleData = mapSinglePayroll(payrolls[0] || {});
      const applicantId = payrolls[0]?.id ?? state.applicantIds[0];
      const getAdjustments = getPayrollAdjustmentsApi(portal);

      let deductionEntries = [];
      let rewardEntries = [];
      try {
        const [deductionsRes, rewardsRes] = await Promise.all([
          getAdjustments({
            eventId,
            params: {
              user_id: applicantId,
              adjustment_type: PAYROLL_ADJUSTMENT_LIST_TYPE_FILTERS.deduction,
              status: PAYROLL_ADJUSTMENT_STATUSES.active,
              page: -1,
              ...adjustmentDateParams,
            },
          }),
          getAdjustments({
            eventId,
            params: {
              user_id: applicantId,
              adjustment_type: PAYROLL_ADJUSTMENT_LIST_TYPE_FILTERS.reward,
              status: PAYROLL_ADJUSTMENT_STATUSES.active,
              page: -1,
              ...adjustmentDateParams,
            },
          }),
        ]);

        deductionEntries = mapPayrollAdjustmentEntries(
          deductionsRes?.data?.data || [],
          { lang, isReward: false, autoDeductionReason },
        );
        rewardEntries = mapPayrollAdjustmentEntries(rewardsRes?.data?.data || [], {
          lang,
          isReward: true,
        });
      } catch {
        // Keep the summary totals; entry lists stay empty if adjustments fail to load.
      }

      return {
        ...singleData,
        deductions: {
          ...singleData.deductions,
          entryCount: deductionEntries.length,
          entries: deductionEntries,
        },
        rewards: {
          ...singleData.rewards,
          entryCount: rewardEntries.length,
          entries: rewardEntries,
        },
      };
    }

    return {
      ...mapBulkPayrollReport(payrolls, {
        totalDeductions: data.total_deductions,
        totalRewards: data.total_rewards,
        totalNetPay: data.total_net_pay,
      }),
      ...buildEventRoleRates(event),
    };
  }, [
    autoDeductionReason,
    event,
    eventId,
    isGenerateAll,
    isSingle,
    lang,
    listFilterParams,
    portal,
    startDate,
    state.applicantIds,
    state.endDate,
  ]);

  useEffect(() => {
    if (!state.open || !state.loading || !eventId) return undefined;

    let cancelled = false;

    loadPayroll()
      .then((data) => {
        if (!cancelled) {
          setState((current) => ({ ...current, loading: false, data }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState((current) => ({ ...current, loading: false, data: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, loadPayroll, state.loading, state.open]);

  const isFirstLangRender = useRef(true);
  useEffect(() => {
    if (isFirstLangRender.current) {
      isFirstLangRender.current = false;
      return;
    }
    setState((current) =>
      current.open ? { ...current, loading: true } : current,
    );
  }, [lang]);

  const reload = useCallback(() => {
    setState((current) =>
      current.open ? { ...current, loading: true, data: null } : current,
    );
  }, []);

  const applicantCount = isGenerateAll
    ? state.data?.applicants?.length ?? 0
    : state.applicantIds.length;

  return {
    open: state.open,
    applicantIds: state.applicantIds,
    loading: state.loading,
    data: state.data,
    isSingle,
    applicantCount,
    startDate,
    endDate: state.endDate,
    openGeneratePayroll: open,
    close,
    reload,
    setStartDate,
    setEndDate,
    setListFilterParams,
  };
};

export default useEventPayrollGeneratePayroll;
